import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, Menu, X, LogOut, User, ChevronDown, ChevronLeft, ChevronRight, BarChart3, Package, ChefHat, Building2, TrendingUp, DollarSign, Settings, Smartphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ScaleFlowLogo from './ScaleFlowLogo.jsx';
import SettingsModal from './SettingsModal.jsx';

const Layout = ({ children, onLanguageChange }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-collapse sidebar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) { // lg breakpoint
        setIsSidebarCollapsed(true);
      }
    };

    // Check initial screen size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const navigation = [
    { name: t('nav.dashboard'), href: '/dashboard', icon: BarChart3 },
    { name: t('nav.materials'), href: '/materials', icon: Package },
    { name: t('nav.recipes'), href: '/dish-costing/recipes', icon: ChefHat },
    { name: t('nav.fixedOverhead'), href: '/dish-costing/fixed-overhead', icon: Building2 },
    { name: t('nav.variableOverhead'), href: '/dish-costing/variable-overhead', icon: TrendingUp },
  ];

  const isActive = (path) => {
    // Special handling for materials - highlight when on any materials-related page
    if (path === '/materials') {
      return location.pathname === '/materials' || location.pathname.startsWith('/materials/');
    }
    // Special handling for recipes (Direct Material Costs) - highlight when on recipes page
    if (path === '/dish-costing/recipes') {
      return location.pathname.startsWith('/dish-costing/recipes');
    }
    // Special handling for fixed overhead - exact match
    if (path === '/dish-costing/fixed-overhead') {
      return location.pathname === '/dish-costing/fixed-overhead';
    }
    // Special handling for variable overhead - exact match
    if (path === '/dish-costing/variable-overhead') {
      return location.pathname === '/dish-costing/variable-overhead';
    }
    // Special handling for pricing - highlight when on any pricing-related page
    if (path === '/pricing') {
      return location.pathname.startsWith('/pricing');
    }
    // Special handling for applications pricing - highlight when on applications pricing page
    if (path === '/applications-pricing') {
      return location.pathname.startsWith('/applications-pricing');
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    onLanguageChange(newLang);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Mobile Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 h-full bg-sidebar shadow-lg border-r border-sidebar-border z-40 transition-all duration-300 ease-in-out ${
        i18n.language === 'ar' ? 'right-0' : 'left-0'
      } ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-0'
      } ${
        isSidebarCollapsed ? 'w-16' : 'w-64'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-sidebar-border bg-sidebar">
            <div className="flex flex-col space-y-4">
              {/* Logo and Title */}
              <div className="flex items-center justify-between">
                {!isSidebarCollapsed && (
                  <ScaleFlowLogo showText={true} />
                )}
                {isSidebarCollapsed && (
                  <ScaleFlowLogo showText={false} className="w-10 h-10" />
                )}
                
                {/* Collapse Button */}
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2 rounded-lg text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 flex-shrink-0"
                  title={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                >
                  {i18n.language === 'ar' ? (
                    isSidebarCollapsed ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />
                  ) : (
                    isSidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Quick Actions */}
              {!isSidebarCollapsed && (
                <div className="flex items-center justify-end pt-2 border-t border-sidebar-border">
                  <div className="text-xs text-sidebar-foreground/60">
                    {i18n.language === 'ar' ? 'إصدار 1.0.0' : 'v1.0.0'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <div className="space-y-1">
              {navigation.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive(item.href)
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/25'
                        : 'text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm'
                    } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    title={isSidebarCollapsed ? item.name : ''}
                  >
                    <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                      <IconComponent className={`h-5 w-5 ${isActive(item.href) ? 'text-sidebar-primary-foreground' : 'text-sidebar-foreground/70 group-hover:text-sidebar-foreground'}`} />
                      {!isSidebarCollapsed && (
                        <span className="font-medium">{item.name}</span>
                      )}
                    </div>
                    {isActive(item.href) && !isSidebarCollapsed && (
                      <div className={`w-2 h-2 bg-sidebar-primary-foreground rounded-full opacity-80 ${i18n.language === 'ar' ? 'mr-auto' : 'ml-auto'}`}></div>
                    )}
                  </Link>
                );
              })}

              {/* Dish Pricing */}
              <div className="mt-2">
                <Link
                  to="/pricing"
                  className={`group flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive('/pricing')
                      ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/25'
                      : 'text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm'
                  } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  title={isSidebarCollapsed ? t('nav.pricing') : ''}
                >
                  <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                    <DollarSign className={`h-5 w-5 ${isActive('/pricing') ? 'text-sidebar-primary-foreground' : 'text-sidebar-foreground/70 group-hover:text-sidebar-foreground'}`} />
                    {!isSidebarCollapsed && (
                      <span className="font-medium">{t('nav.pricing')}</span>
                    )}
                  </div>
                  {isActive('/pricing') && !isSidebarCollapsed && (
                      <div className={`w-2 h-2 bg-sidebar-primary-foreground rounded-full opacity-80 ${i18n.language === 'ar' ? 'mr-auto' : 'ml-auto'}`}></div>
                  )}
                </Link>
              </div>

              {/* Applications Pricing */}
              <div className="mt-2">
                <Link
                  to="/applications-pricing"
                  className={`group flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive('/applications-pricing')
                      ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/25'
                      : 'text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm'
                  } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  title={isSidebarCollapsed ? t('nav.applicationsPricing') : ''}
                >
                  <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'} ${isSidebarCollapsed ? 'justify-center' : ''}`}>
                    <Smartphone className={`h-5 w-5 ${isActive('/applications-pricing') ? 'text-sidebar-primary-foreground' : 'text-sidebar-foreground/70 group-hover:text-sidebar-foreground'}`} />
                    {!isSidebarCollapsed && (
                      <span className="font-medium">{t('nav.applicationsPricing')}</span>
                    )}
                  </div>
                  {isActive('/applications-pricing') && !isSidebarCollapsed && (
                      <div className={`w-2 h-2 bg-sidebar-primary-foreground rounded-full opacity-80 ${i18n.language === 'ar' ? 'mr-auto' : 'ml-auto'}`}></div>
                  )}
                </Link>
              </div>
            </div>
          </nav>

          {/* Settings Button - Only show when sidebar is expanded */}
          {!isSidebarCollapsed && (
            <div className="p-4 border-t border-sidebar-border">
              <button
                onClick={() => setIsSettingsModalOpen(true)}
                className="w-full flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm"
              >
                <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                  <Settings className="h-5 w-5 text-sidebar-foreground/70" />
                  <span className="font-medium">{t('settings.title')}</span>
                </div>
              </button>
            </div>
          )}

          {/* User Menu */}
          <div className="p-4 border-t border-sidebar-border mt-auto">
            {/* User Info Card - Clickable with dropdown */}
            {!isSidebarCollapsed && (
              <div className="relative mb-3" ref={userMenuRef}>
                <button
                  className="w-full p-3 bg-sidebar-accent rounded-lg border border-sidebar-border hover:bg-sidebar-accent/80 transition-all duration-200 shadow-sm"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <div className="w-10 h-10 bg-sidebar-primary/20 rounded-full flex items-center justify-center shadow-sm">
                      <User className="h-5 w-5 text-sidebar-primary" />
                    </div>
                    <div className={`flex-1 min-w-0 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <p className="text-sm font-semibold text-sidebar-foreground truncate">
                        {user?.username}
                      </p>
                      <p className={`text-xs text-sidebar-foreground/70 flex items-center ${i18n.language === 'ar' ? 'justify-end' : ''}`}>
                        <span className={`w-2 h-2 bg-green-400 rounded-full ${i18n.language === 'ar' ? 'ml-5' : 'mr-2'}`}></span>
                        {user?.role || t('nav.activeUser')}
                      </p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-sidebar-foreground/70 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                
                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className={`absolute ${i18n.language === 'ar' ? 'left-0' : 'right-0'} bottom-full mb-2 w-48 bg-card rounded-lg shadow-lg border border-border py-1 z-50`}>
                    <div className="px-2 py-1 text-xs text-muted-foreground border-b border-border">
                      {t('nav.userActions')}
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsUserMenuOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-sm text-card-foreground hover:bg-muted hover:text-destructive flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2 text-right' : 'space-x-2 text-left'} transition-colors duration-200`}
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`${i18n.language === 'ar' 
        ? (isSidebarCollapsed ? 'mr-16' : 'lg:mr-64') 
        : (isSidebarCollapsed ? 'ml-16' : 'lg:ml-64')
      } ${i18n.language === 'ar' ? 'ml-0' : 'mr-0'}`}>
        {/* Main Content */}
        <main className="p-6 bg-background min-h-screen">
          {children}
        </main>
      </div>

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={isSettingsModalOpen} 
        onClose={() => setIsSettingsModalOpen(false)}
        onLanguageChange={onLanguageChange}
      />
    </div>
  );
};

export default Layout; 